import React, { useContext, useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { InlineMath } from "react-katex";
import { ThemeContext } from "../../../context/ThemeContext";
import { graphThemes } from "../../../themes/graphThemes";
import SelectInput from "../../inputs/SelectInput";

const letterFromIndex = (index) => String.fromCharCode(65 + index);

export default function InteractivePointQuiz({
  targetPoint,
  value,
  onChange,
  onAnswer,
  locked,
  index = 0,
  showPrompt = true,
  hideLocationSelect = false,
  validation,
}) {
  const { x: tx, y: ty } = targetPoint;
  const { theme } = useContext(ThemeContext);
  const style = graphThemes[theme === "dark" ? "cyberpunk" : "classic"];

  const [clicked, setClicked] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState("");

  useEffect(() => {
    if (!value || typeof value !== "object") {
      setClicked(null);
      setSelectedAnswer("");
      return;
    }

    const p = value.point;
    if (p && typeof p.x === "number" && typeof p.y === "number") {
      setClicked({ x: p.x, y: p.y });
    } else {
      setClicked(null);
    }

    setSelectedAnswer(typeof value.location === "string" ? value.location : "");
  }, [value]);

  const emit = (nextPoint, nextLocation) => {
    const base = value && typeof value === "object" ? value : {};
    const payload = { ...base, point: nextPoint || null, location: nextLocation || null };

    if (onChange) onChange(payload);
    if (onAnswer) onAnswer(payload);
  };

  const handleClick = (event) => {
    if (locked) return;

    const { x, y } = event.points[0];
    const rx = Math.round(x - 0.5);
    const ry = Math.round(y - 0.5);
    const newPoint = { x: rx, y: ry };

    setClicked(newPoint);
    emit(newPoint, selectedAnswer || null);
  };

  const handleSelectChange = (e) => {
    const v = e.target.value;
    setSelectedAnswer(v);
    emit(clicked, v);
  };

  const options = [
    { value: "I ćwiartka", label: "I ćwiartka" },
    { value: "II ćwiartka", label: "II ćwiartka" },
    { value: "III ćwiartka", label: "III ćwiartka" },
    { value: "IV ćwiartka", label: "IV ćwiartka" },
    { value: "Na dodatniej części osi X", label: "Na dodatniej części osi X" },
    { value: "Na ujemnej części osi X", label: "Na ujemnej części osi X" },
    { value: "Na dodatniej części osi Y", label: "Na dodatniej części osi Y" },
    { value: "Na ujemnej części osi Y", label: "Na ujemnej części osi Y" },
    { value: "W początku układu", label: "W początku układu" },
  ];

  const backgroundGrid = {
    x: [],
    y: [],
    type: "scatter",
    mode: "markers",
    marker: { size: 35, color: "black", opacity: 0.001 },
    hoverinfo: "none",
    showlegend: false,
  };

  if (!validation) {
    for (let i = -6; i <= 6; i += 0.5) {
      for (let j = -6; j <= 6; j += 0.5) {
        backgroundGrid.x.push(parseFloat(i.toFixed(1)));
        backgroundGrid.y.push(parseFloat(j.toFixed(1)));
      }
    }
  }

  const clickTrace =
    clicked && !validation
      ? {
          x: [clicked.x],
          y: [clicked.y],
          type: "scatter",
          mode: "markers",
          marker: { size: 12, color: "deepskyblue" },
          hoverinfo: "skip",
          showlegend: false,
        }
      : null;

  const validationTraces = [];
  if (validation) {
    const correctPoint = validation.correctPoint || { x: tx, y: ty };
    const isCorrect = !!validation.isCorrect;

    if (clicked) {
      if (isCorrect) {
        validationTraces.push({
          x: [clicked.x],
          y: [clicked.y],
          type: "scatter",
          mode: "markers",
          marker: { size: 14, color: "limegreen" },
          hoverinfo: "skip",
          showlegend: false,
        });
      } else {
        validationTraces.push({
          x: [clicked.x],
          y: [clicked.y],
          type: "scatter",
          mode: "markers",
          marker: { size: 14, color: "crimson" },
          hoverinfo: "skip",
          showlegend: false,
        });
        validationTraces.push({
          x: [correctPoint.x],
          y: [correctPoint.y],
          type: "scatter",
          mode: "markers",
          marker: { size: 14, color: "limegreen" },
          hoverinfo: "skip",
          showlegend: false,
        });
      }
    } else {
      validationTraces.push({
        x: [correctPoint.x],
        y: [correctPoint.y],
        type: "scatter",
        mode: "markers",
        marker: { size: 14, color: "limegreen" },
        hoverinfo: "skip",
        showlegend: false,
      });
    }
  }

  const axisAnnotations = [
    {
      x: 6.15,
      y: 0,
      ax: 5.7,
      ay: 0,
      xref: "x",
      yref: "y",
      axref: "x",
      ayref: "y",
      text: "",
      showarrow: true,
      arrowhead: 3,
      arrowsize: 1.2,
      arrowwidth: 2,
      arrowcolor: style.axisColor,
    },
    {
      x: 0,
      y: 6.15,
      ax: 0,
      ay: 5.7,
      xref: "x",
      yref: "y",
      axref: "x",
      ayref: "y",
      text: "",
      showarrow: true,
      arrowhead: 3,
      arrowsize: 1.2,
      arrowwidth: 2,
      arrowcolor: style.axisColor,
    },
    {
      x: 5.88,
      y: -0.22,
      xref: "x",
      yref: "y",
      text: "x",
      showarrow: false,
      font: { color: style.axisColor, size: 14 },
      xanchor: "left",
      yanchor: "top",
    },
    {
      x: 0.36,
      y: 5.86,
      xref: "x",
      yref: "y",
      text: "y",
      showarrow: false,
      font: { color: style.axisColor, size: 14 },
      xanchor: "left",
      yanchor: "bottom",
    },
  ];

  const layout = {
    xaxis: {
      range: [-6, 6],
      dtick: 1,
      zeroline: true,
      zerolinewidth: 3,
      zerolinecolor: style.axisColor,
      gridcolor: style.gridColor,
      linecolor: style.axisColor,
      linewidth: 2,
      scaleanchor: "y",
    },
    yaxis: {
      range: [-6, 6],
      dtick: 1,
      zeroline: true,
      zerolinewidth: 3,
      zerolinecolor: style.axisColor,
      gridcolor: style.gridColor,
      linecolor: style.axisColor,
      linewidth: 2,
      scaleanchor: "x",
    },
    margin: { t: 20, b: hideLocationSelect ? 20 : 40, l: 40, r: 20 },
    paper_bgcolor: style.backgroundColor,
    plot_bgcolor: style.backgroundColor,
    font: { color: style.axisColor },
    hovermode: "closest",
    showlegend: false,
    annotations: axisAnnotations,
  };

  return (
    <div>
      {showPrompt && (
        <p style={{ fontSize: "1.05rem", marginBottom: "8px" }}>
          Zaznacz na układzie współrzędnych, gdzie leży punkt{" "}
          <InlineMath math={`${letterFromIndex(index)} = \\left(${tx},\\ ${ty}\\right)`} /> i określ jego położenie.
        </p>
      )}

      <div style={{ width: 330, height: 330, margin: "0 auto" }}>
        <Plot
          data={[
            ...(!validation ? [backgroundGrid] : []),
            ...(clickTrace ? [clickTrace] : []),
            ...validationTraces,
          ]}
          layout={layout}
          config={{ scrollZoom: false, displayModeBar: false }}
          onClick={handleClick}
          style={{ width: "100%", height: "100%" }}
        />
      </div>

      {!hideLocationSelect && (
        <div style={{ marginTop: "12px", textAlign: "left" }}>
          <p>
            <strong>Gdzie znajduje się zaznaczony punkt?</strong>
          </p>
          <div style={{ maxWidth: "300px" }}>
            <SelectInput value={selectedAnswer} onChange={handleSelectChange} disabled={locked} options={options} />
          </div>
        </div>
      )}
    </div>
  );
}
